/**
 * templateLibrary.js — aggregates every drafted template.
 *
 * A template is a starting point a founder takes to their attorney. The renderer prepends the
 * standard disclaimer to every rendered document; nothing here overrides that.
 *
 * Exactly one template per id, one id per file. `manufacturing-nda` lives in
 * confidentialityTemplates.js with the rest of the NDA set.
 */

import { CONFIDENTIALITY_TEMPLATES } from "./templates/confidentialityTemplates.js";
import { PRODUCT_DEVELOPMENT_TEMPLATES } from "./templates/productDevelopmentTemplates.js";
import { SUPPLY_TEMPLATES } from "./templates/supplyTemplates.js";
import { MANUFACTURING_TEMPLATES } from "./templates/manufacturingTemplates.js";
import { COMMERCIAL_TEMPLATES } from "./templates/commercialTemplates.js";
import { DISTRIBUTION_TEMPLATES } from "./templates/distributionTemplates.js";
import { PARTNERSHIP_TEMPLATES } from "./templates/partnershipTemplates.js";
import { PEOPLE_TEMPLATES } from "./templates/peopleTemplates.js";
import { MARKETING_TEMPLATES } from "./templates/marketingTemplates.js";
import { EVENTS_TEMPLATES } from "./templates/eventsTemplates.js";
import { NOTICE_TEMPLATES } from "./templates/noticeTemplates.js";

const SOURCES = [
  ["confidentiality", CONFIDENTIALITY_TEMPLATES],
  ["product-development", PRODUCT_DEVELOPMENT_TEMPLATES],
  ["supply", SUPPLY_TEMPLATES],
  ["manufacturing", MANUFACTURING_TEMPLATES],
  ["commercial", COMMERCIAL_TEMPLATES],
  ["distribution", DISTRIBUTION_TEMPLATES],
  ["partnership", PARTNERSHIP_TEMPLATES],
  ["people", PEOPLE_TEMPLATES],
  ["marketing", MARKETING_TEMPLATES],
  ["events", EVENTS_TEMPLATES],
  ["notice", NOTICE_TEMPLATES],
];

// A duplicate id is a mistake, not something to resolve silently — two drafts of one template is
// exactly the situation where the wrong one ships and nobody notices. Fail loudly instead.
const byId = new Map();
for (const [source, list] of SOURCES) {
  for (const t of list) {
    if (byId.has(t.id)) {
      throw new Error(
        `Duplicate template id "${t.id}": defined in both ${byId.get(t.id)._source} and ${source}. ` +
          `Delete one — do not filter it out at runtime.`,
      );
    }
    byId.set(t.id, { ...t, _source: source });
  }
}

export const TEMPLATES = [...byId.values()];
export const TEMPLATE_BY_ID = Object.fromEntries(TEMPLATES.map((t) => [t.id, t]));
export const TEMPLATE_BY_DOC_ID = Object.fromEntries(TEMPLATES.map((t) => [t.docId, t]));
export const TEMPLATE_COUNT = TEMPLATES.length;

export function templateForDoc(docId) {
  return TEMPLATE_BY_DOC_ID[docId] || null;
}

export function hasTemplate(docId) {
  return Boolean(TEMPLATE_BY_DOC_ID[docId]);
}
